import { useState } from 'react';
import { toast } from 'sonner';
import type { JobRole, RoleCategory } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useArchiveRole, useRestoreRole, useUpdateRole } from '../../hooks/useRoles';
import { Button } from '../../components/ui/Button';
import { SapRoleDefaultsCard } from './sap/SapRoleDefaultsCard';

const inputClass =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

interface RoleFormProps {
  role: JobRole;
  categories: RoleCategory[];
  /** Called after a successful save or a cancel. */
  onDone?: () => void;
}

/**
 * Inline editor for one role, mounted within its list row so editing never
 * scrolls away from the row being changed. Roles are no longer created here:
 * they are born in SAP (Sync with SAP) or free-typed in the new-employee
 * wizard, so this form only edits what the app owns (category, and name /
 * external ref for roles SAP does not manage).
 */
export function RoleForm({ role, categories, onDone }: RoleFormProps) {
  const update = useUpdateRole();
  const archive = useArchiveRole();
  const restore = useRestoreRole();

  const [name, setName] = useState(role.name);
  const [externalRef, setExternalRef] = useState(role.external_ref ?? '');
  const [categoryId, setCategoryId] = useState(role.category_id ?? '');
  const [archived, setArchived] = useState(!!role.archived_at);

  // SAP owns the identity of a synced role; the API rejects renames too.
  const sapManaged = !!(role.external_ref && role.sap_synced_at);

  async function onSave() {
    if (!name.trim()) return;
    try {
      // For a SAP-managed role only the category is editable here; the
      // API rejects renames and the sync owns the external ref.
      await update.mutateAsync(
        sapManaged
          ? { id: role.id, category_id: categoryId || null }
          : {
              id: role.id,
              name: name.trim(),
              external_ref: externalRef.trim() || null,
              category_id: categoryId || null,
            },
      );
      if (archived !== !!role.archived_at) {
        await (archived ? archive : restore).mutateAsync(role.id);
      }
      toast.success('Role updated');
      onDone?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save role');
    }
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClass + (sapManaged ? ' opacity-60' : '')}
          autoFocus={!sapManaged}
          disabled={sapManaged}
        />
        {sapManaged ? (
          <span className="mt-1 block text-xs text-fg-dim">
            Synced from SAP (job {role.external_ref}); rename it in SAP and sync again.
          </span>
        ) : null}
      </label>
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">
          Category <span className="font-normal text-fg-dim">(optional)</span>
        </span>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className={inputClass}
        >
          <option value="">No category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-fg-dim">
          The bucket this role falls under (e.g. Engineer). Category-level kit and
          onboarding templates apply to every role in the bucket.
        </span>
      </label>
      {sapManaged ? null : (
        <label className="block text-sm">
          <span className="font-medium text-fg-muted">
            External reference <span className="font-normal text-fg-dim">(optional)</span>
          </span>
          <input
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            placeholder="External key, if any"
            className={inputClass + ' font-mono text-xs'}
          />
          <span className="mt-1 block text-xs text-fg-dim">
            Key from the source system. The SAP sync fills this with the SAP JobID
            automatically; leave blank unless you know the key.
          </span>
        </label>
      )}
      <label className="flex items-start gap-2.5 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={archived}
          onChange={(e) => setArchived(e.target.checked)}
        />
        <span>
          <span className="font-medium text-fg-muted">Archived</span>
          <span className="mt-0.5 block text-xs text-fg-dim">
            Hidden from role lookups (wizard, person and template pickers). People already
            holding it keep it, and SAP data keeps syncing quietly. Reversible.
          </span>
        </span>
      </label>
      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          loading={update.isPending || archive.isPending || restore.isPending}
          disabled={!name.trim()}
        >
          Save changes
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
      <SapRoleDefaultsCard roleId={role.id} />
    </div>
  );
}
